<template>
  <div class="slider">
    <div class="slide">
      <div class="bar" :style="progressBarStyle">
        <div class="track">
          <div class="pipe"></div>
          <div class="pipe"></div>
          <div class="pipe"></div>
        </div>
      </div>
      <div class="listener" ref="listener">
        <input
          tabindex="-1"
          aria-hidden="true"
          @keydown.right="changeToRight"
          @keydown.left="changeToLeft"
          type="range"
          :style="rangeStyle"
          @input="change"
          @change="change"
          :value="barSize"
          @click="change"
        />
      </div>
    </div>

    <div class="labels" :style="labelsStyle">
      <span
        role="button"
        :aria-pressed="value == index ? 'true' : 'false'"
        @keydown.enter.space="choiceSelected(index)"
        :tabindex="index + 4"
        v-for="(label, index) in labelsToDisplay"
        :class="label.class"
        :style="label.style"
        :key="index"
        @click="changeAccessibility(index)"
      >
        {{ label.text }}
      </span>
    </div>
  </div>
</template>

<script>
import { getValueByScore } from '@/directives/metricColor.js'
import colors from '@/styles/custom/_colors.scss'

function calculateLabelArea(barSize, steps) {
  const rounded = Math.floor((barSize * steps) / 100)
  return rounded === steps ? steps - 1 : rounded
}

function isMobile() {
  return window.innerWidth < 767
}

const defaultData = {
  barSize: 0,
  selected: null,
  defaultState: {
    color: '#ccc',
  },
  rangeStyle: {},
  eventHandler: null,
  acessibilityAlertWarning: '',
}

const props = {
  value: {
    type: Number | Object,
    required: false,
    default: null,
  },
  labels: {
    type: Array,
    required: false,
    default: function() {
      return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
    },
    validator: function(labels) {
      return labels.length > 1
    },
  },
  states: {
    type: Array,
    required: false,
    default: function() {
      return [
        {
          labels: [0, 1, 2, 3, 4],
          color: '#ff6767',
        },
        {
          labels: [7],
          color: '#ff7700',
        },
        {
          labels: [8, 9, 10],
          color: '#33e44a',
        },
      ]
    },
    validator: function(states) {
      const shouldHaveLabelAndColorProperties = state => {
        return state.hasOwnProperty('labels') && state.hasOwnProperty('color')
      }

      return (
        states.filter(shouldHaveLabelAndColorProperties).length ===
        states.length
      )
    },
  },
}

export default {
  name: 'BaseSlider',
  props: props,
  data: function() {
    return defaultData
  },
  watch: {
    selected: function(value) {
      this.$emit('input', value)
    },
    value: function(val) {
      if (parseInt(this.selected) === parseInt(val)) return

      if (val === null && this.selected !== null) {
        return this.$emit('input', this.selected)
      }

      this.barSize = 0
      this.selected = null

      if (!isNaN(val)) {
        const value = (val * 100) / this.labels.length
        this.setBarState(value)
      }
    },
  },

  created: function() {
    this.barSize = 0
    this.selected = null
  },

  mounted: function() {
    this.$nextTick(() => {
      this.calculateBar()
      this.eventHandler = window.addEventListener('resize', () => {
        this.calculateBar()
      })
    })

    if (this.value) {
      const value = this.value * 10
      this.setBarState(value)
    }
  },

  computed: {
    progressBarStyle: function() {
      const v = this.barSize > 59 ? 3 : 0.5 // fix the progressbar image
      const size = {
        height: `calc(${this.barSize - v}% + 35px)`,
        width: `calc(${this.barSize - v}% + 35px)`,
      }
      return Object.assign(
        this.value !== null ? { background: this.labelsStyle } : {},
        size
      )
    },

    labelsStyle: function() {
      const valueColors = {
        low: colors['metric-low'],
        medium: colors['metric-medium'],
        high: colors['metric-high'],
        blank: colors['metric-blank'],
      }

      return getValueByScore(this.value, valueColors)
    },

    labelsToDisplay: function() {
      const stateColor = index =>
        index === this.selected ? this.labelsStyle : 'inherit'
      const labelAsObject = label =>
        typeof label === 'object' ? label : { text: label }
      const labelClassByIndex = index => {
        if (index < this.selected) return 'less'

        return index === this.selected ? 'active' : 'more'
      }

      return this.labels.map((label, index) => {
        return Object.assign(labelAsObject(label), {
          class: labelClassByIndex(index), // less | current | more
          style: { color: stateColor(index) },
        })
      })
    },

    currentState: function() {
      const selected = this.labels[this.selected]
      const labelValue =
        typeof selected !== 'object' ? parseInt(selected) : selected.text

      const labelForSelected = this.selected !== null ? labelValue : ''
      const stateWithSelected = state => state.labels.includes(labelForSelected)
      const stateOrDefaultState = (defaultState, state) => state

      return this.states
        .filter(stateWithSelected)
        .reduce(stateOrDefaultState, this.defaultState)
    },
  },

  destroyed: function() {
    delete this.eventHandler
  },

  methods: {
    calculateBar() {
      if (!this.$refs['listener'] || !isMobile()) {
        this.rangeStyle = {}
        return
      }

      this.rangeStyle = {
        width: `${this.$refs['listener'].clientHeight - 10}px`,
        height: `${this.$refs['listener'].clientWidth}px`,
        transform: 'rotate(270deg)',
        position: 'absolute',
        opacity: '0',
      }
    },

    change(event) {
      this.setBarState(event.target.value)
    },

    changeAccessibility(value) {
      this.setBarState(value * 10)
    },

    changeToLeft() {
      const value = parseInt(this.barSize) - 9
      if (value > 0) {
        this.setBarState(value)
      } else {
        this.setBarState(0)
      }
    },

    changeToRight() {
      const value = parseInt(this.barSize) + 9
      if (value < 100) {
        this.setBarState(value)
      } else {
        this.setBarState(100)
      }
    },

    setBarState(value) {
      const steps = this.labels.length
      this.barSize = value
      this.selected = calculateLabelArea(this.barSize, steps)
    },

    choiceSelected(value) {
      this.setBarState(value * 10)
      this.$emit('input', value)
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

.slider {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-wrap: wrap;
  overflow: hidden;
  width: 90%;

  @media (max-width: 767px) {
    flex-flow: row nowrap;
  }

  .slide {
    background: map-get($theme-colors, 'gray-light');
    border: 1px solid map-get($theme-colors, 'gray01');
    border-radius: 60px;
    display: flex;
    height: 70px;
    justify-content: flex-start;
    position: relative;
    width: 100%;

    @media (max-width: 767px) {
      width: 60px;
      height: inherit;
    }

    .listener {
      bottom: 0;
      left: 0;
      position: absolute;
      right: 0;
      top: 0;

      input[type='range']::-webkit-slider-thumb {
        -webkit-appearance: none;
        height: 70px;
        width: 70px;
        border-radius: 50%;
      }

      input {
        opacity: 0;
        width: 100%;
        height: 100%;
      }

      @media (max-width: 767px) {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .bar {
      background: map-get($theme-colors, 'gray05');
      bottom: 0;
      border-radius: 60px;
      left: 0;
      min-width: 68px;
      min-height: 68px;
      max-width: 100%;
      max-height: 100%;
      position: absolute;
      transition: background ease 0.3s;
      top: 0;

      @media (max-width: 767px) {
        min-width: 100%;
        top: inherit;
        bottom: 0;
        left: 0;
        min-height: 58px;
      }

      .track {
        align-items: center;
        background: white;
        border-radius: 40px;
        bottom: 9px;
        box-shadow: 1px 1px 3px 2px rgba(47, 42, 42, 0.2);
        cursor: pointer;
        display: flex;
        justify-content: center;
        left: calc(100% - 60px);
        position: absolute;
        right: 9px;
        top: 9px;

        @media (max-width: 767px) {
          bottom: calc(100% - 50px);
          left: 9px;
          transform: rotateZ(90deg);
        }

        .pipe {
          background: map-get($theme-colors, 'gray05');
          border-radius: 4px;
          height: 70%;
          margin: 0 2px;
          width: 5px;

          &:first-child,
          &:last-child {
            height: 55%;
          }
        }
      }
    }
  }

  .labels {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin: 0 auto;
    width: 95%;

    @media (max-width: 767px) {
      flex-direction: column-reverse;
      margin: 0 10px;
      width: auto;
      min-width: 25px;
    }

    span {
      transition: all ease 0.3s;
      padding: 0;

      &.active {
        font-weight: 500;
        font-size: 1.2rem;
        padding: 0rem;
      }

      @media (max-height: 650px) {
        font-size: 0.8rem;
        padding: 0.1rem;

        &.active {
          font-size: 0.9rem;
        }
      }
    }
  }
}
</style>
